import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Clock, TrendingUp, AlertTriangle, AlertOctagon, X, Wallet } from 'lucide-react';
import api from '../utils/api';

const AgingReport = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        if (user?.role === 'shipment_staff') {
            navigate('/shipments');
            return;
        }

        const fetchReport = async () => {
            setIsLoading(true);
            try {
                const response = await api.get('/collections/aging');
                setReportData(response.data);
            } catch (error) {
                console.error("Bekleyen Tahsilatlar verisi alınamadı", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchReport();
    }, [user, navigate]);

    // Para biçimlendirme
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-full">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-t-2 border-primary-500 animate-spin"></div>
                        <div className="absolute inset-2 rounded-full border-r-2 border-blue-500 animate-spin animate-reverse"></div>
                    </div>
                </div>
            </Layout>
        );
    }

    const cards = [
        { label: '0-3 Gün Bekleyen', key: '0-3', icon: <Clock size={24} className="text-primary-400" />, borderColor: 'border-primary-500/30' },
        { label: '4-5 Gün Bekleyen', key: '4-5', icon: <Clock size={24} className="text-blue-400" />, borderColor: 'border-blue-500/30' },
        { label: '6-7 Gün Geciken', key: '6-7', icon: <TrendingUp size={24} className="text-yellow-400" />, borderColor: 'border-yellow-500/30' },
        { label: '8-10 Gün Geciken', key: '8-10', icon: <AlertTriangle size={24} className="text-orange-400" />, borderColor: 'border-orange-500/30' },
        { label: '10+ Gün Kritik', key: '10+', icon: <AlertOctagon size={24} className="text-red-400" />, borderColor: 'border-red-500/50', bgGlow: 'bg-red-500/5' },
    ];

    return (
        <Layout>
            <div className="mb-8">
                <h1 className="text-xl md:text-3xl font-bold text-white mb-2">Bekleyen Tahsilatlar Raporu</h1>
                <p className="text-slate-400 text-sm md:text-base">Tahsil edilmemiş (bekleyen) alacakların bekleme süresine göre risk analizi.</p>
            </div>

            {/* Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                {cards.map((card) => {
                    const data = reportData ? reportData[card.key] : { total: 0, count: 0 };
                    return (
                        <div key={card.key} className={`glass p-6 rounded-2xl border ${card.borderColor} ${card.bgGlow || ''} relative overflow-hidden transition-transform hover:-translate-y-1`}>
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <p className="text-sm font-medium text-slate-400">{card.label}</p>
                                    <h3 className="text-xl md:text-3xl font-bold text-white mt-1">{formatAmount(data.total)}</h3>
                                </div>
                                <div className="p-3 bg-slate-800/50 rounded-xl">
                                    {card.icon}
                                </div>
                            </div>
                            <div className="text-xs font-semibold text-slate-300 bg-slate-800/80 inline-block px-3 py-1 rounded-full border border-slate-700">
                                {data.count} Adet Kayıt
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Detay Tablosu */}
            <div className="glass border border-slate-700/50 rounded-2xl overflow-hidden shadow-2xl">
                <div className="p-5 border-b border-slate-700/50 bg-slate-800/30">
                    <h3 className="text-lg font-bold text-white">Bekleyen İşlemlerin Detayları</h3>
                </div>
                <div className="mobile-table-hint lg:hidden">Risk kaydının tüm ayrıntıları için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-800/80 text-slate-300 border-b border-slate-700">
                            <tr>
                                <th className="px-3 md:px-6 py-4 font-semibold">Risk Grubu</th>
                                <th className="px-3 md:px-6 py-4 font-semibold">Tutar</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden sm:table-cell">Satıcı ve Alıcı</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden md:table-cell">Kurum</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden sm:table-cell">Gün</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden lg:table-cell">Kayıt Tarihi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                            {reportData?.items?.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-8 text-slate-500">Tebrikler! Bekleyen hiç alacağınız yok.</td></tr>
                            ) : (
                                reportData?.items?.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-800/30 cursor-pointer transition-colors" onClick={() => setSelectedItem(item)}>
                                        <td className="px-3 md:px-6 py-4">
                                            <span className={`px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[10px] md:text-xs font-bold border ${item.daysPending <= 3 ? 'bg-primary-500/10 text-primary-400 border-primary-500/20' :
                                                item.daysPending <= 5 ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                                    item.daysPending <= 7 ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                                                        item.daysPending <= 10 ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                                                            'bg-red-500/10 text-red-500 border-red-500/20'
                                                }`}>
                                                {item.ageCategory}
                                            </span>
                                        </td>
                                        <td className="px-3 md:px-6 py-4 font-bold text-white text-xs md:text-sm">
                                            {formatAmount(item.remainingAmount !== null ? item.remainingAmount : item.amount)} {item.currency}
                                        </td>
                                        <td className="px-3 md:px-6 py-4 hidden sm:table-cell">
                                            <div className="text-white text-xs md:text-sm">{item.seller} <span className="text-slate-500 mx-1">→</span> {item.buyer}</div>
                                        </td>
                                        <td className="px-3 md:px-6 py-4 text-slate-300 text-xs md:text-sm hidden md:table-cell">{item.institution}</td>
                                        <td className="px-3 md:px-6 py-4 font-mono font-bold text-slate-300 text-xs hidden sm:table-cell">{item.daysPending} Gün</td>
                                        <td className="px-3 md:px-6 py-4 text-slate-400 text-xs hidden lg:table-cell">{new Date(item.createdAt).toLocaleDateString('tr-TR')}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>


            {/* DETAY MODALI */}
            {selectedItem && (
                <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} formatAmount={formatAmount} />
            )}

        </Layout>
    );
};

// Detay Modal Bileşeni (inline)
const ItemDetailModal = ({ item, onClose, formatAmount }) => {
    if (!item) return null;

    const riskColor = item.daysPending <= 3 ? 'text-primary-400' :
        item.daysPending <= 5 ? 'text-blue-400' :
            item.daysPending <= 7 ? 'text-yellow-400' :
                item.daysPending <= 10 ? 'text-orange-400' : 'text-red-500';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-slate-800 border border-slate-700 w-full max-w-lg rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center p-5 border-b border-slate-700/50">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                        <Wallet size={20} className={riskColor} /> Bekleyen İşlem Detayı
                    </h2>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Satıcı</p>
                            <p className="text-white font-semibold">{item.seller}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Alıcı</p>
                            <p className="text-white font-semibold">{item.buyer}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Kurum</p>
                            <p className="text-slate-200">{item.institution}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Döviz</p>
                            <p className="text-slate-200">{item.currency}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Asıl Tutar</p>
                            <p className="text-white font-bold text-lg">{formatAmount(item.amount)} {item.currency}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Kalan Bakiye</p>
                            <p className="text-orange-400 font-bold text-lg">{formatAmount(item.remainingAmount !== null ? item.remainingAmount : item.amount)} {item.currency}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Bekleme Süresi</p>
                            <p className={`font-bold ${riskColor}`}>{item.daysPending} Gündür Bekliyor</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1">Risk Grubu</p>
                            <p className={`font-bold ${riskColor}`}>{item.ageCategory}</p>
                        </div>
                        <div className="col-span-2">
                            <p className="text-xs text-slate-400 mb-1">Kayıt Tarihi</p>
                            <p className="text-slate-200">{new Date(item.createdAt).toLocaleDateString('tr-TR')} {new Date(item.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        {item.transferNo && (
                            <div className="col-span-2">
                                <p className="text-xs text-slate-400 mb-1">Transfer No</p>
                                <p className="text-primary-400 font-mono">{item.transferNo}</p>
                            </div>
                        )}
                        {item.notes && (
                            <div className="col-span-2">
                                <p className="text-xs text-slate-400 mb-1">Notlar</p>
                                <p className="text-slate-300 text-sm">{item.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgingReport;
